/**
 * Hermes Chatbot Placeholder
 *
 * This component displays a chat icon in the bottom-right corner.
 * Currently shows "Coming Soon" as we cannot guarantee client data isolation
 * without additional backend work.
 *
 * Future implementation will need:
 * - Tenant-aware context injection
 * - Auth-scoped API endpoints
 * - Protection against prompt injection for cross-client data leakage
 */

import { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';

export function HermesChatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const { language } = useLanguage();
  const t = language === 'es';

  return (
    <>
      {/* Floating chat button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-primary-600 hover:bg-primary-500 rounded-full shadow-lg flex items-center justify-center text-white transition-all hover:scale-105 z-40"
        title={t ? 'Asistente Hermes' : 'Hermes Assistant'}
      >
        <HermesIcon className="w-7 h-7" />
      </button>

      {/* Coming Soon Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />

          {/* Modal */}
          <div className="relative bg-surface-800 border border-surface-400/30 rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 bg-gradient-to-r from-primary-600/20 to-primary-500/10 border-b border-surface-400/20">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-primary-500/20 rounded-full flex items-center justify-center">
                  <HermesIcon className="w-6 h-6 text-primary-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">Hermes</h2>
                  <p className="text-sm text-text-secondary">
                    {t ? 'Tu asistente de simulación' : 'Your simulation assistant'}
                  </p>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-surface-700 rounded-full flex items-center justify-center">
                <span className="text-3xl">🚀</span>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">
                {t ? 'Próximamente' : 'Coming Soon'}
              </h3>
              <p className="text-text-secondary text-sm mb-4">
                {t
                  ? 'Hermes estará disponible pronto para ayudarte a entender tus datos y guiarte a través de las simulaciones.'
                  : 'Hermes will be available soon to help you understand your data and guide you through simulations.'}
              </p>

              {/* Features preview */}
              <div className="bg-surface-700/50 rounded-lg p-4 mb-4">
                <p className="text-xs text-text-tertiary uppercase tracking-wider mb-3">
                  {t ? 'Capacidades' : 'Capabilities'}
                </p>
                <div className="space-y-2 text-left">
                  {[
                    { icon: '💡', text: t ? 'Explicar métricas y gráficos' : 'Explain metrics and charts' },
                    { icon: '🎯', text: t ? 'Guiar configuración de simulaciones' : 'Guide simulation setup' },
                    { icon: '📊', text: t ? 'Interpretar resultados' : 'Interpret results' },
                    { icon: '🔍', text: t ? 'Responder preguntas sobre tus datos' : 'Answer questions about your data' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-text-secondary">
                      <span>{item.icon}</span>
                      <span>{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              <p className="text-xs text-text-tertiary">
                {t
                  ? 'Estamos asegurando la privacidad de tus datos antes del lanzamiento.'
                  : 'We are ensuring your data privacy before launch.'}
              </p>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-surface-700/30 border-t border-surface-400/20">
              <button
                onClick={() => setIsOpen(false)}
                className="w-full py-2.5 bg-surface-600 hover:bg-surface-500 text-white rounded-lg transition-colors text-sm font-medium"
              >
                {t ? 'Entendido' : 'Got it'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function HermesIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
      {/* Stylized messenger/chat icon with wings (Hermes reference) */}
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
      />
      {/* Small wing accents */}
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M1 8l2-2M1 10l1-1M23 8l-2-2M23 10l-1-1"
        strokeWidth={1}
      />
    </svg>
  );
}

export default HermesChatbot;
